import React, { useContext, useState } from 'react';
import StepCard from './StepCard';
import { Form, Input, Upload, Alert, Spin } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import { useIntl } from 'react-intl';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import { VerifiedIdOrCodeStatusEnum } from '@nxsys/constants/AppEnums';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import StepStateContext from './StepStateContext';

interface AddShareCodeProps {
  stepNumber: number;
  shareCode?: string | null;
  haveShareCode?: boolean;
  verifiedIdOrCodeStatus?: VerifiedIdOrCodeStatusEnum;
  documentID?: string | null;
  completed?: boolean;
  onSubmitStep: (
    values: any,
    options?: { hideToast?: boolean; skipComplete?: boolean },
  ) => void;
}

type AddShareCodeValues = {
  shareCodeValue?: string;
  documentIdValue?: UploadFile[];
  haveShareCodeValue?: boolean;
};

const normFile = (e: any): UploadFile[] => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList ?? [];
};

const AddShareCode: React.FC<AddShareCodeProps> = ({
  stepNumber,
  shareCode,
  haveShareCode,
  verifiedIdOrCodeStatus,
  documentID,
  completed = false,
  onSubmitStep,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm<AddShareCodeValues>();
  const { messages } = useIntl();
  const watchHaveShareCode = Form.useWatch('haveShareCodeValue', form);
  // Ticking the checkbox saves on its own, and that card has no Save button, so
  // the checkbox is the only place that save can show progress.
  const { submitting } = useContext(StepStateContext);
  const isDone = !!(verifiedIdOrCodeStatus === VerifiedIdOrCodeStatusEnum.DONE);
  // Covers the S3 upload below, which happens before onSubmitStep and so is not
  // covered by the onboarding modal's loader.
  const [saving, setSaving] = useState(false);

  const onFinish = async (values: AddShareCodeValues) => {
    let params = {};
    const haveShareCodeValue = values.haveShareCodeValue;
    if (!haveShareCodeValue) {
      params = { haveShareCode: false };
    } else {
      let url = null;
      // An already-saved document is seeded into the fileList as `{ name }`
      // with no `originFileObj`, so only upload when the user actually picked
      // a new file — otherwise keep the existing documentID.
      const newFile = values.documentIdValue?.[0]?.originFileObj as
        | File
        | undefined;
      if (newFile) {
        try {
          setSaving(true);
          const res = await dispatch(
            commonActions.uploadFileToS3({ file: newFile }),
          ).unwrap();
          url = res?.url;
        } finally {
          setSaving(false);
        }
      }
      params = {
        haveShareCode: true,
        shareCode: values.shareCodeValue,
        documentID: url ?? documentID,
      };
    }
    onSubmitStep(params);
  };

  // Dedicated checkbox handler. `checked` = "I don't have a Share Code" ticked,
  // so haveShareCode is the inverse. Persists the flag immediately in both
  // toggle directions via onSubmitStep directly — bypassing form.submit() so
  // the share-code `required` rule (enforced only on Save) doesn't fire here.
  const handleToggleHaveShareCode = (checked: boolean) => {
    const haveShareCode = !checked;
    form.setFieldValue('haveShareCodeValue', haveShareCode);
    // Reset any uploaded document on toggle — it belongs to the share-code
    // path and should not carry over across the toggle.
    form.setFieldValue('documentIdValue', []);
    // The no-share-code card has no Save button, so this checkbox is the step's
    // only submit. It must be able to finalise the onboarding when addShareCode
    // is the last step, otherwise there is no way to close it.
    if (checked) {
      onSubmitStep({ haveShareCode });
      return;
    }
    // Unticking only records the flag — the share code is still to be entered
    // and saved, so keep it off the "success" endpoint.
    onSubmitStep({ haveShareCode }, { hideToast: true, skipComplete: true });
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.addShareCode'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={
        watchHaveShareCode
          ? [
              {
                variant: 'outlined',
                name: messages['common.save'] as string,
                onClick: () => form.submit(),
                loading: saving,
              },
            ]
          : []
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          shareCodeValue: shareCode,
          haveShareCodeValue: haveShareCode,
          documentIdValue: documentID
            ? [
                {
                  name: documentID,
                },
              ]
            : [],
        }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        {watchHaveShareCode && (
          <>
            <Form.Item
              label={messages['prospect.onboarding.shareCodeOrEc'] as string}
              name="shareCodeValue"
              rules={[
                {
                  required: true,
                  message: messages[
                    'prospect.onboarding.shareCode.required'
                  ] as string,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const haveShareCodeValue = getFieldValue('haveShareCodeValue');
                    if (!haveShareCodeValue) return Promise.resolve();
                    // Skip pattern check when empty — the `required` rule handles it.
                    if (!value) return Promise.resolve();
                    // Exactly 9 alphanumeric chars — spaces not allowed.
                    if (!/^[A-Z0-9]{9}$/i.test(String(value))) {
                      return Promise.reject(
                        new Error(
                          (messages[
                            'prospect.onboarding.shareCode.invalid'
                          ] as string) ||
                            'Your share code must be exactly 9 characters long and can only contain letters (A-Z) and numbers (0-9).',
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                placeholder={
                  messages[
                    'prospect.onboarding.shareCode.placeholder'
                  ] as string
                }
                maxLength={15}
              />
            </Form.Item>

            <Form.Item
              label={messages['prospect.onboarding.documentId'] as string}
              name="documentIdValue"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload.Dragger
                name="files"
                beforeUpload={() => false}
                multiple={false}
                maxCount={1}
                accept=".svg,.png,.jpg,.jpeg,.gif"
              >
                <p className="ant-upload-text">
                  {messages['prospect.onboarding.upload.text'] as string}
                </p>
                <p className="ant-upload-hint">
                  {messages['prospect.onboarding.upload.hint'] as string}
                </p>
              </Upload.Dragger>
            </Form.Item>
          </>
        )}
        {!watchHaveShareCode && (
          <Alert
            message={
              isDone
                ? (messages['prospect.onboarding.verified'] as string)
                : (messages['prospect.onboarding.pendingVerify'] as string)
            }
            type={isDone ? 'success' : 'warning'}
            showIcon
            className="mb-6"
          />
        )}
        <Form.Item
          name="haveShareCodeValue"
        >
          <AppCheckbox
            size="small"
            checked={!watchHaveShareCode}
            onChange={(e) => handleToggleHaveShareCode(e.target.checked)}
          >
            <span className="inline-flex items-center gap-2">
              {messages['prospect.onboarding.noShareCode'] as string}
              {submitting && <Spin size="small" />}
            </span>
          </AppCheckbox>
        </Form.Item>
      </AppFormContainer>
    </StepCard>
  );
};

export default AddShareCode;
